import { memo, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Types
import type { TemplateById } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useGoToRoute from 'hooks/GoToRoute';
import useQueryParams from 'hooks/UseQueryParams';
// Import Components
import TemplateInfoModalBody from './TemplateInfoModalBody';
// Import UI components
import { Modal, Button, Tooltip } from '@geneui/components';
import ActionLink from 'components/common/ActionLink';
// Import Helpers
import { injectContentUrl } from 'services/Helpers';
// Import Services
import { TemplateHttpService } from 'services/http';
import { groupDynamicVariablesByDisplayName, replaceBodyTopImageUrlPlaceholders } from 'services/template';
// Import Constants
import { ModalsActions, TemplateTypesLabels, l } from 'constants/common';
import { RoutesList } from 'routes';
// Import SCSS
import 'assets/scss/segmentModal.scss';
// Import Version History
import {
    VersionHistoryIconWithPopover,
    VersionHistorySelectedVersionLabel,
    VersionHistoryProviderHOC,
    useVersionHistoryContext,
} from 'components/VersionHistory';
// Import Enums
import { ObjectTypeEnum } from 'enums';

const {
    TEMPLATE_CLONE_EMAIL,
    TEMPLATE_CLONE_SMS,
    TEMPLATE_CLONE_HOORY,
    TEMPLATE_CLONE_INTERNAL_MESSAGE,
    TEMPLATE_CLONE_SOCIAL_TELEGRAM,
    TEMPLATE_CLONE_PUSH_NOTIFICATION,
    TEMPLATE_CLONE_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_DYNAMIC_POP_UP,
    TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_CMS_BANNER,
    TEMPLATE_CLONE_SOCIAL_VIBER,
    TEMPLATE_CLONE_SOCIAL_WHATS_APP,
    TEMPLATE_EDIT_EMAIL,
    TEMPLATE_EDIT_SMS,
    TEMPLATE_EDIT_HOORY,
    TEMPLATE_EDIT_INTERNAL_MESSAGE,
    TEMPLATE_EDIT_SOCIAL_TELEGRAM,
    TEMPLATE_EDIT_PUSH_NOTIFICATION,
    TEMPLATE_EDIT_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_DYNAMIC_POP_UP,
    TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_CMS_BANNER,
    TEMPLATE_EDIT_SOCIAL_VIBER,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP,
} = RoutesList;

interface Props {
    templateId?: number;
    isVisible?: boolean;
    isPredefined?: boolean;
    isGlobal?: boolean;
    handleClose?: (action: string) => void;
    withTitleActions?: boolean;
    predefinedKeyValue?: object;
}

const TemplateInfoModal: React.FC<Props> = ({
    templateId,
    isPredefined,
    isVisible,
    isGlobal,
    handleClose,
    withTitleActions = true,
    predefinedKeyValue,
}) => {
    const { t } = useTranslation();
    const { getRoutePath } = useGoToRoute();
    const notificationQueryParams = useQueryParams('notification');
    const { doGetRequest } = useRequest();

    const [isLoading, setIsLoading] = useState(false);
    const [templateInfo, setTemplateInfo] = useState<Nullable<TemplateById>>(null);
    const { getTemplate, getTemplateModels } = useCancelableRequests({
        getTemplate: TemplateHttpService.getTemplate,
        getTemplateModels: TemplateHttpService.getTemplateModels,
    });

    const { pointInTime, isHistoryMode } = useVersionHistoryContext();

    const clonePath = useMemo(() => {
        const { DeliveryMethod, TemplateId, EventId } = templateInfo ?? {};
        const params = { templateId: TemplateId, EventId: EventId };
        const queryString = isNil(EventId)
            ? null
            : notificationQueryParams.encode({ eventId: EventId, selectedChannel: DeliveryMethod });

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                return getRoutePath(TEMPLATE_CLONE_EMAIL, params, queryString);
            case TemplateTypesLabels.SMS:
                return getRoutePath(TEMPLATE_CLONE_SMS, params, queryString);
            case TemplateTypesLabels.Hoory:
                return getRoutePath(TEMPLATE_CLONE_HOORY, params, queryString);
            case TemplateTypesLabels.Internal:
                return getRoutePath(TEMPLATE_CLONE_INTERNAL_MESSAGE, params, queryString);
            case TemplateTypesLabels.Telegram:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_TELEGRAM, params, queryString);
            case TemplateTypesLabels.PushNotification:
                return getRoutePath(TEMPLATE_CLONE_PUSH_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.PopUp:
                return getRoutePath(TEMPLATE_CLONE_POP_UP_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.DynamicPopUp:
                return getRoutePath(TEMPLATE_CLONE_DYNAMIC_POP_UP, params, queryString);
            case TemplateTypesLabels.CMSPopUp:
                return getRoutePath(TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.CMSBanner:
                return getRoutePath(TEMPLATE_CLONE_CMS_BANNER, params, queryString);
            case TemplateTypesLabels.Viber:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_VIBER, params, queryString);
            case TemplateTypesLabels.WhatsApp:
                return getRoutePath(TEMPLATE_CLONE_SOCIAL_WHATS_APP, params, queryString);
            default:
        }
    }, [getRoutePath, notificationQueryParams, templateInfo]);

    const closeCloneModal = () => {
        handleClose?.(ModalsActions.CLONE);
    };

    const editPath = useMemo(() => {
        const { DeliveryMethod, TemplateId, EventId } = templateInfo ?? {};
        const params = { templateId: TemplateId };
        const queryString = isNil(EventId) ? null : notificationQueryParams.encode({ eventId: EventId });

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                return getRoutePath(TEMPLATE_EDIT_EMAIL, params, queryString);
            case TemplateTypesLabels.SMS:
                return getRoutePath(TEMPLATE_EDIT_SMS, params, queryString);
            case TemplateTypesLabels.Hoory:
                return getRoutePath(TEMPLATE_EDIT_HOORY, params, queryString);
            case TemplateTypesLabels.Internal:
                return getRoutePath(TEMPLATE_EDIT_INTERNAL_MESSAGE, params, queryString);
            case TemplateTypesLabels.Telegram:
                return getRoutePath(TEMPLATE_EDIT_SOCIAL_TELEGRAM, params, queryString);
            case TemplateTypesLabels.PushNotification:
                return getRoutePath(TEMPLATE_EDIT_PUSH_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.PopUp:
                return getRoutePath(TEMPLATE_EDIT_POP_UP_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.DynamicPopUp:
                return getRoutePath(TEMPLATE_EDIT_DYNAMIC_POP_UP, params, queryString);
            case TemplateTypesLabels.CMSPopUp:
                return getRoutePath(TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION, params, queryString);
            case TemplateTypesLabels.CMSBanner:
                return getRoutePath(TEMPLATE_EDIT_CMS_BANNER, params, queryString);
            case TemplateTypesLabels.Viber:
                return getRoutePath(TEMPLATE_EDIT_SOCIAL_VIBER, params, queryString);
            case TemplateTypesLabels.WhatsApp:
                return getRoutePath(TEMPLATE_EDIT_SOCIAL_WHATS_APP, params, queryString);
            default:
        }
    }, [getRoutePath, notificationQueryParams, templateInfo]);

    const closeEditModal = () => {
        handleClose?.(ModalsActions.EDIT);
    };

    const getTitleKey = () => {
        return isPredefined ? l.PredefinedTemplateInfo : l.TemplateInfo; // TODO: need write label by type of template see the segment info modal code
    };

    const cleanUp = () => {
        return () => {
            setTemplateInfo(null);
            setIsLoading(false);
        };
    };

    const fetchDynamicVariables = async (templateType: number, eventId: Nullable<number>) => {
        return new Promise((resolve, reject) => {
            doGetRequest(getTemplateModels.request, {
                successCallback: (Data) => {
                    const groupedDynamicVariables = groupDynamicVariablesByDisplayName([
                        { type: TemplateTypesLabels[templateType], variables: Data },
                    ]);
                    resolve(groupedDynamicVariables);
                },
                queryString: { channel: templateType, eventId: eventId },
                errorCallback: reject,
            });
        });
    };

    const getTemplateInfo = async (templateId, pointInTime): Promise<TemplateById> => {
        return new Promise((resolve, reject) => {
            doGetRequest(getTemplate.request, {
                queryString: { templateId, pointInTime },
                successCallback: async (Data) => {
                    let parsedBody = injectContentUrl(Data.Body);

                    if ([TemplateTypesLabels.Email, TemplateTypesLabels.Internal].includes(Data.DeliveryMethod)) {
                        const templateDynamicVariables = await fetchDynamicVariables(
                            Data.DeliveryMethod,
                            Data.EventId ?? 0,
                        );
                        parsedBody = replaceBodyTopImageUrlPlaceholders(
                            parsedBody,
                            templateDynamicVariables,
                            TemplateTypesLabels[Data.DeliveryMethod],
                        );
                    }
                    resolve({ ...Data, Body: parsedBody });
                },
                errorCallback: (error) => {
                    reject(error);
                },
            });
        });
    };

    useEffect(() => {
        setIsLoading(true);
        getTemplateInfo(templateId, pointInTime)
            .then((data) => {
                setTemplateInfo(data);
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                // eslint-disable-next-line no-console
                console.log(error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, pointInTime]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <Modal
            closeOnClickOutside={true}
            onCancel={() => handleClose?.(ModalsActions.CLOSE)}
            withPortal={true}
            background="dark-background"
            position="center"
            visible={isVisible}
            closable={false}
            size="content-size"
            className="crm-object-list-modal crm-template-info-modal"
            title={
                <div className="crm-object-list-modal-head">
                    <span className="crm-object-list-modal-h-text">{t(getTitleKey())}</span>
                    {withTitleActions && (
                        <div className="crm-object-list-modal-h-icons">
                            <VersionHistorySelectedVersionLabel
                                objectId={templateId}
                                objectTypeId={ObjectTypeEnum.Template}
                            />
                            {!isNil(templateInfo) && (
                                <>
                                    <VersionHistoryIconWithPopover
                                        objectId={templateId}
                                        objectTypeId={ObjectTypeEnum.Template}
                                    />
                                    {!isHistoryMode && (
                                        <Tooltip text={t(l.CloneTemplate)}>
                                            <ActionLink
                                                icon="bc-icon-clone"
                                                appearance="minimal"
                                                color="primary"
                                                redirectPath={clonePath || ''}
                                                onRedirect={closeCloneModal}
                                            />
                                        </Tooltip>
                                    )}
                                    {!isPredefined && !isGlobal && !isHistoryMode && (
                                        <Tooltip text={t(l.EditTemplate)}>
                                            <ActionLink
                                                icon="bc-icon-edit"
                                                appearance="minimal"
                                                color="primary"
                                                redirectPath={editPath || ''}
                                                onRedirect={closeEditModal}
                                            />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            }
            footer={
                <Button appearance="minimal" onClick={() => handleClose?.(ModalsActions.CLOSE)}>
                    {t(l.Close)}
                </Button>
            }
        >
            <TemplateInfoModalBody
                isLoading={isLoading}
                isPredefined={isPredefined}
                childItemsInfo={templateInfo?.ChildItemsInfo}
                getTemplateInfo={getTemplateInfo}
                templateInfo={templateInfo}
                templateId={templateId}
                withActionButtons={false}
                predefinedKeyValue={predefinedKeyValue}
            />
        </Modal>
    );
};

TemplateInfoModal.displayName = 'TemplateInfoModal';

export default memo(VersionHistoryProviderHOC(TemplateInfoModal));
